import React, { useEffect, useRef, useState } from 'react';

// ── Signing with a finger ──────────────────────────────────────────────────
//
// A canvas a technician draws on with his thumb, in a van, one-handed.
//
// Three things that decide whether it works there rather than on a desk:
//
//   The canvas is sized in DEVICE pixels and scaled back down, or the line
//   comes out blurred on every phone made since 2014.
//
//   touch-action: none in the CSS, or the browser scrolls the page instead of
//   drawing, and the signature comes out as three disconnected dots.
//
//   Pointer events rather than touch and mouse separately, so the same code
//   handles a finger, a stylus and a mouse without three sets of handlers
//   disagreeing about where the line is.
export default function SignaturePad({ onChange, disabled }) {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const dirty = useRef(false);
  const [hasInk, setHasInk] = useState(false);

  // Size to the element's real pixels, and resize whenever that changes.
  //
  // A ResizeObserver rather than a window resize listener, because the case
  // that actually bites is not the window changing. Every technician screen is
  // in the DOM at once and hidden with CSS, so this canvas mounts at zero by
  // zero, and a width listener never fires when it later becomes visible. The
  // pad looked fine and silently recorded nothing: a technician would sign,
  // tap the button, and be told to sign in the box he had just signed in.
  //
  // The observer covers that, plus rotation and the on-screen keyboard
  // pushing the layout around.
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;

    const fit = () => {
      const rect = canvas.getBoundingClientRect();
      if (!rect.width || !rect.height) return;          // still hidden
      const ratio = window.devicePixelRatio || 1;
      const want = { w: Math.round(rect.width * ratio), h: Math.round(rect.height * ratio) };
      if (canvas.width === want.w && canvas.height === want.h) return;
      // Resizing a canvas clears it, so anything already drawn would be lost.
      if (dirty.current) return;
      canvas.width = want.w;
      canvas.height = want.h;
      const ctx = canvas.getContext('2d');
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.scale(ratio, ratio);
      ctx.lineWidth = 2.2;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      // Read the ink colour off the page so it inverts with the theme.
      ctx.strokeStyle = getComputedStyle(canvas).color || '#111';
    };

    fit();
    let observer;
    if (typeof ResizeObserver !== 'undefined') {
      observer = new ResizeObserver(fit);
      observer.observe(canvas);
    }
    window.addEventListener('orientationchange', fit);
    return () => {
      observer?.disconnect();
      window.removeEventListener('orientationchange', fit);
    };
  }, []);

  const pointFrom = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  };

  const start = (e) => {
    if (disabled) return;
    e.preventDefault();
    const canvas = canvasRef.current;
    // Last line of defence: if the pad somehow still has no backing pixels,
    // nothing would be drawn and the failure would be invisible.
    if (!canvas.width || !canvas.height) return;
    canvas.setPointerCapture?.(e.pointerId);
    const ctx = canvas.getContext('2d');
    const p = pointFrom(e);
    ctx.beginPath();
    ctx.moveTo(p.x, p.y);
    drawing.current = true;
  };

  const move = (e) => {
    if (!drawing.current) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext('2d');
    const p = pointFrom(e);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    if (!dirty.current) { dirty.current = true; setHasInk(true); }
  };

  const end = (e) => {
    if (!drawing.current) return;
    drawing.current = false;
    canvasRef.current.releasePointerCapture?.(e.pointerId);
    // Hand the drawing up only when the stroke finishes. Emitting on every
    // pixel would re-encode a PNG two hundred times across one signature.
    onChange?.(dirty.current ? canvasRef.current.toDataURL('image/png') : '');
  };

  const clear = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    dirty.current = false;
    setHasInk(false);
    onChange?.('');
  };

  return (
    <div className="sig">
      <canvas ref={canvasRef} className="sig-pad"
        onPointerDown={start} onPointerMove={move}
        onPointerUp={end} onPointerCancel={end} onPointerLeave={end} />
      <div className="sig-base">
        <span className="sig-x">✕</span>
        <span className="sig-hint">{hasInk ? 'Your signature' : 'Sign here with your finger'}</span>
        <button type="button" className="sig-clear" onClick={clear} disabled={!hasInk}>Clear</button>
      </div>
    </div>
  );
}
